import "./styles.css";

import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useScreen } from "Screen/ScreenContext";
import ChargebackDetails from "./ChargebackDetails";

import {
  IconAlertTriangle,
  IconBuildingBank,
  IconCreditCard,
  IconMenu2,
  IconMoneybag,
  IconReceipt,
  IconRepeat,
  IconWallet,
  IconX,
} from "@tabler/icons-react";

import BankOnboardingCard from "./components/BankOnboardingCard";
import WalletConnectionCards from "./components/WalletConnectionCards";
import api from "@helpers/api";
import ChargesHistorySection from "./tabs/Charges";
import SubscriptionsSection from "./tabs/Subscriptions";
import WithdrawalSection from "./tabs/Withdrawals";
import ChargebacksSection from "./tabs/Chargebacks";
import { HelpdeskSupport, Text } from "@fragments";
import {
  useWallets,
  WALLET_PROVIDERS,
  walletIsConnected,
} from "@helpers/wallets";

const bankingScreens = [
  {
    key: "wallets",
    label: "Minha carteira",
    sidebarTitle: "Carteiras nacional e internacional",
    screenTitle: "Minha carteira",
    title: "Carteiras nacional e internacional",
    subtitle:
      "Acompanhe suas contas de recebimento, saldo disponível e configurações de saque.",
    screenUrl: "banking/wallet",
    path: "/banking/wallet",
    showInSidebar: true,
    icon: <IconWallet />,
    match: (path) => path === "/banking" || path.includes("/banking/wallet"),
  },
  {
    key: "history",
    label: "Cobranças",
    sidebarTitle: "Histórico de cobranças",
    screenTitle: "Cobranças",
    title: "Histórico de cobranças",
    subtitle: "Consulte pagamentos, status e métodos usados pelos membros.",
    screenUrl: "banking/charges",
    path: "/banking/charges",
    showInSidebar: true,
    icon: <IconReceipt />,
    match: (path) => path.includes("/banking/charges"),
  },
  {
    key: "subscriptions",
    label: "Assinaturas",
    sidebarTitle: "Planos recorrentes",
    screenTitle: "Assinaturas",
    title: "Planos recorrentes",
    subtitle: "Veja assinantes ativos, cancelados e expirados por comunidade.",
    screenUrl: "banking/subscriptions",
    path: "/banking/subscriptions",
    showInSidebar: true,
    icon: <IconRepeat />,
    match: (path) => path.includes("/banking/subscriptions"),
  },
  {
    key: "withdrawals",
    label: "Saques",
    sidebarTitle: "Histórico e conta conectada",
    screenTitle: "Saques",
    title: "Histórico de saques e conta conectada",
    subtitle:
      "Gerencie a conta conectada, acompanhe taxas e acompanhe o histórico de transferências.",
    screenUrl: "banking/withdrawal",
    path: "/banking/withdrawal",
    showInSidebar: true,
    icon: <IconMoneybag />,
    match: (path) => path.includes("/banking/withdrawal"),
  },
  {
    key: "chargebacks",
    label: "Chargebacks",
    sidebarTitle: "Contestações em aberto",
    screenTitle: "Chargebacks",
    title: "Contestações em aberto",
    subtitle: "Acompanhe disputas, prazos de contestação e resultados.",
    screenUrl: "banking/chargebacks",
    path: "/banking/chargebacks",
    showInSidebar: true,
    icon: <IconAlertTriangle />,
    match: (path) => path.includes("/banking/chargebacks"),
  },
  {
    key: "anticipations",
    label: "Antecipações",
    screenTitle: "Antecipações",
    title: "Solicitações de antecipação",
    subtitle:
      "Acompanhe solicitações futuras quando este módulo estiver disponível.",
    path: "/banking/anticipations",
    showInSidebar: false,
    icon: <IconCreditCard />,
    match: (path) => path.includes("/banking/anticipations"),
  },
  {
    key: "commissions",
    label: "Comissões",
    sidebarTitle: "Repasses de afiliados",
    disabled: true,
    badge: "Em breve",
    showInSidebar: true,
    icon: <IconCreditCard />,
  },
];

const WALLET_HELPDESK_ARTICLES = [
  {
    title: "Como criar planos para vender sua comunidade",
    subtitle: "Veja onde acessar e configurar seus planos.",
    url: "https://help.ginastee.com/pt-br/vendas-planos-e-cupons/como-criar-planos-para-vender-sua-comunidade#onde-acessar",
    icon: <IconReceipt />,
  },
  {
    title: "Como configurar sua Wallet no Ginastee",
    subtitle: "Entenda o status de analise da sua carteira.",
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-configurar-sua-wallet-no-ginastee#status-de-analise",
    icon: <IconWallet />,
  },
  {
    title: "Como solicitar um saque",
    subtitle: "Confira o passo a passo para pedir uma transferencia.",
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-solicitar-um-saque",
    icon: <IconMoneybag />,
  },
];

function providerFromSearch(searchParams) {
  return searchParams.get("provider") === WALLET_PROVIDERS.STRIPE
    ? WALLET_PROVIDERS.STRIPE
    : WALLET_PROVIDERS.ASAAS;
}

export default function Banking() {
  document.title = "Banking | Ginastee";

  const { chargebackId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBreadcrumb, setFullWidth } = useScreen();
  const returnUrl = searchParams.get("return");

  const {
    asaas,
    stripe,
    loading: loadingWallets,
    refresh: refreshWallets,
    hasAnyConnected,
  } = useWallets();

  const [provider, setProvider] = useState(providerFromSearch(searchParams));
  const [showAsaasOnboarding, setShowAsaasOnboarding] = useState(false);
  const selectedWallet = provider === WALLET_PROVIDERS.STRIPE ? stripe : asaas;

  useEffect(() => {
    setFullWidth(true);
    return () => setFullWidth(false);
  }, [setFullWidth]);

  function handleWalletApproved() {
    refreshWallets();
    setShowAsaasOnboarding(false);
    if (returnUrl) navigate(returnUrl);
  }

  function handleProviderChange(nextProvider) {
    setProvider(nextProvider);
    if (nextProvider !== WALLET_PROVIDERS.ASAAS) {
      setShowAsaasOnboarding(false);
    }
    const nextParams = new URLSearchParams(searchParams);
    // asaas is the URL default — only a stripe selection is worth carrying.
    if (nextProvider === WALLET_PROVIDERS.STRIPE) {
      nextParams.set("provider", nextProvider);
    } else {
      nextParams.delete("provider");
    }
    setSearchParams(nextParams, { replace: true });
  }

  function handleOpenAsaasOnboarding() {
    setShowAsaasOnboarding(true);
  }

  useEffect(() => {
    const urlProvider = providerFromSearch(searchParams);
    if (urlProvider !== provider) setProvider(urlProvider);
  }, [location.search]);

  useEffect(() => {
    if (!returnUrl || loadingWallets || !hasAnyConnected) return;
    navigate(returnUrl, { replace: true });
  }, [returnUrl, loadingWallets, hasAnyConnected, navigate]);

  useEffect(() => {
    if (walletIsConnected(asaas)) {
      setShowAsaasOnboarding(false);
    }
  }, [asaas?.connected]);

  const getInitialScreen = () => {
    const foundScreen = bankingScreens.find(
      (screen) => screen.match && screen.match(location.pathname),
    );
    return foundScreen ? foundScreen.key : "wallets";
  };

  const [screen, setScreen] = useState(getInitialScreen());

  useEffect(() => {
    const newScreen = getInitialScreen();
    if (newScreen !== screen) {
      setScreen(newScreen);
    }
  }, [location.pathname, screen]);

  const activeScreenConfig =
    bankingScreens.find((t) => t.key === screen) || bankingScreens[0];

  useEffect(() => {
    if (chargebackId) return;
    const items = [
      {
        label: "Banking",
        href: "/banking",
        icon: <IconBuildingBank size={18} />,
      },
      ...(activeScreenConfig?.label
        ? [{ label: activeScreenConfig.label, href: activeScreenConfig.path }]
        : []),
    ];
    setBreadcrumb(items);
    return () => setBreadcrumb(null);
  }, [activeScreenConfig?.key, chargebackId]);

  const [chargebackOpenCount, setChargebackOpenCount] = useState(0);

  useEffect(() => {
    api
      .get("charge/chargeback/list?page=1&per_page=1")
      .then(({ data }) => {
        setChargebackOpenCount(data?.summary?.in_progress?.charges ?? 0);
      })
      .catch(() => {});
  }, []);

  function renderActiveScreen() {
    if (screen === "wallets") {
      return (
        <WalletScreen
          asaas={asaas}
          stripe={stripe}
          loading={loadingWallets}
          showAsaasOnboarding={showAsaasOnboarding}
          onProviderChange={handleProviderChange}
          onOpenAsaasOnboarding={handleOpenAsaasOnboarding}
          onRefresh={refreshWallets}
          onWalletApproved={handleWalletApproved}
        />
      );
    }

    if (screen === "withdrawals") {
      return (
        <WithdrawalSection
          provider={provider}
          wallet={selectedWallet}
          loadingWallet={loadingWallets}
          reloadWallets={refreshWallets}
          onProviderChange={handleProviderChange}
        />
      );
    }

    if (screen === "subscriptions")
      return <SubscriptionsSection provider={provider} />;
    if (screen === "chargebacks")
      return <ChargebacksSection provider={provider} />;
    return <ChargesHistorySection provider={provider} />;
  }

  if (chargebackId) {
    return <ChargebackDetails chargebackId={chargebackId} />;
  }

  return (
    <div className="banking-page">
      <div className="banking-layout">
        <BankingSidebar
          items={bankingScreens.filter((item) => item.showInSidebar)}
          currentScreen={screen}
          provider={provider}
          searchParams={searchParams}
          chargebackOpenCount={chargebackOpenCount}
          navigate={navigate}
        />

        <main className="banking-content">
          <div className="banking-screen-content pb-9">
            <div className="banking-page-heading">
              <Text.h4 contrast={6} weight={500} left={activeScreenConfig.icon}>
                {activeScreenConfig.label || activeScreenConfig.screenTitle}
              </Text.h4>
              <Text.h1 weight={500} contrast={10}>
                {activeScreenConfig.title}
              </Text.h1>
              <Text.p contrast={5}>{activeScreenConfig.subtitle}</Text.p>
            </div>

            {renderActiveScreen()}
          </div>
        </main>
      </div>
    </div>
  );
}

function WalletScreen({
  asaas,
  stripe,
  loading,
  showAsaasOnboarding,
  onProviderChange,
  onOpenAsaasOnboarding,
  onRefresh,
  onWalletApproved,
}) {
  return (
    <div className="banking-wallet-screen">
      <WalletConnectionCards
        asaas={asaas}
        stripe={stripe}
        loading={loading}
        onProviderChange={onProviderChange}
        onOpenAsaasOnboarding={onOpenAsaasOnboarding}
        onRefresh={onRefresh}
      />

      {showAsaasOnboarding && !walletIsConnected(asaas) && (
        <div className="banking-wallet-setup-panel">
          <BankOnboardingCard onApproved={onWalletApproved} />
        </div>
      )}

      <HelpdeskSupport.CardsGroup cards={WALLET_HELPDESK_ARTICLES} />
    </div>
  );
}

function BankingSidebar({
  items,
  currentScreen,
  provider,
  searchParams,
  chargebackOpenCount,
  navigate,
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  function buildPath(path) {
    const nextParams = new URLSearchParams(searchParams);
    // asaas is the URL default — only a stripe selection is worth carrying.
    if (provider === WALLET_PROVIDERS.STRIPE) {
      nextParams.set("provider", provider);
    } else {
      nextParams.delete("provider");
    }
    const query = nextParams.toString();
    return `${path}${query ? `?${query}` : ""}`;
  }

  return (
    <>
      <div
        className={`banking-sidebar-backdrop${open ? " is-visible" : ""}`}
        onClick={close}
      />
      <aside className={`banking-sidebar${open ? " is-open" : ""}`}>
        <button
          type="button"
          className="banking-sidebar__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Fechar menu de banking" : "Abrir menu de banking"}
        >
          {open ? <IconX size={18} /> : <IconMenu2 size={18} />}
        </button>

        <div className="banking-sidebar__header">
          <Text.h5 contrast={9} weight={400}>
            Banking
          </Text.h5>
          <Text.caption contrast={6}>Gerencie seu faturamento</Text.caption>
        </div>

        <nav className="banking-sidebar__menu">
          {items.map((item) => (
            <BankingSidebarButton
              key={item.key}
              item={item}
              active={currentScreen === item.key}
              disabled={item.disabled}
              countBadge={
                item.key === "chargebacks" && chargebackOpenCount > 0
                  ? chargebackOpenCount
                  : undefined
              }
              onClick={() => {
                if (item.disabled || !item.path) return;
                close();
                navigate(buildPath(item.path));
              }}
            />
          ))}
        </nav>
      </aside>
    </>
  );
}

function BankingSidebarButton({ item, active, disabled, countBadge, onClick }) {
  return (
    <button
      type="button"
      className={`banking-sidebar__item${active ? " active" : ""}${disabled ? " disabled" : ""}`}
      onClick={onClick}
      title={item.label}
      disabled={disabled}
    >
      <span className="banking-sidebar__item-icon">{item.icon}</span>
      <span className="banking-sidebar__item-copy">
        <Text.h6 contrast={active ? 8 : 7} weight={400}>
          {item.label}
        </Text.h6>
        <Text.span contrast={active ? 7 : 6}>{item.sidebarTitle}</Text.span>
      </span>
      {(item.badge || countBadge) && (
        <span className="banking-sidebar__badge">
          {countBadge || item.badge}
        </span>
      )}
    </button>
  );
}
